/**
 * Normalized API error.
 *
 * The backend uses gRPC-Gateway, which returns errors in the Google RPC
 * `Status` shape: { code: number, message: string, details?: [...] }.
 * The HTTP status is derived from the gRPC code. We preserve both so flows can
 * branch on either (PRD §10.3).
 */
export class ApiError extends Error {
  /** HTTP status code (e.g. 401, 403). 0 when the request never reached a server. */
  readonly httpStatus: number;
  /** gRPC status code as a number (e.g. 16 for UNAUTHENTICATED), if known. */
  readonly grpcCode?: number;
  /** gRPC status name (e.g. "UNAUTHENTICATED"), if known. */
  readonly grpcStatus?: string;
  /** Raw details payload from the Status object. */
  readonly details?: unknown;
  /** True when the failure was a network/offline error rather than an HTTP response. */
  readonly isNetworkError: boolean;
  /** Retry-After header value in seconds, when present (429). */
  readonly retryAfterSeconds?: number;

  constructor(init: {
    message: string;
    httpStatus: number;
    grpcCode?: number;
    grpcStatus?: string;
    details?: unknown;
    isNetworkError?: boolean;
    retryAfterSeconds?: number;
  }) {
    super(init.message);
    this.name = "ApiError";
    this.httpStatus = init.httpStatus;
    this.grpcCode = init.grpcCode;
    this.grpcStatus = init.grpcStatus;
    this.details = init.details;
    this.isNetworkError = init.isNetworkError ?? false;
    this.retryAfterSeconds = init.retryAfterSeconds;
  }

  get isUnauthorized(): boolean {
    return this.httpStatus === 401;
  }
  get isForbidden(): boolean {
    return this.httpStatus === 403;
  }
  get isNotFound(): boolean {
    return this.httpStatus === 404;
  }
  get isConflict(): boolean {
    return this.httpStatus === 409;
  }
  get isRateLimited(): boolean {
    return this.httpStatus === 429;
  }
  /** gRPC FAILED_PRECONDITION (9) maps to HTTP 400 but carries a distinct code. */
  get isFailedPrecondition(): boolean {
    return this.grpcCode === 9 || this.grpcStatus === "FAILED_PRECONDITION";
  }
}

const GRPC_STATUS_NAMES: Record<number, string> = {
  1: "CANCELLED",
  2: "UNKNOWN",
  3: "INVALID_ARGUMENT",
  4: "DEADLINE_EXCEEDED",
  5: "NOT_FOUND",
  6: "ALREADY_EXISTS",
  7: "PERMISSION_DENIED",
  8: "RESOURCE_EXHAUSTED",
  9: "FAILED_PRECONDITION",
  10: "ABORTED",
  11: "OUT_OF_RANGE",
  12: "UNIMPLEMENTED",
  13: "INTERNAL",
  14: "UNAVAILABLE",
  15: "DATA_LOSS",
  16: "UNAUTHENTICATED",
};

interface StatusLike {
  code?: number;
  message?: string;
  details?: unknown;
}

/** Parse a Google RPC Status body into an ApiError. */
export function apiErrorFromStatus(status: StatusLike, httpStatus: number, retryAfterSeconds?: number): ApiError {
  const grpcCode = typeof status.code === "number" ? status.code : undefined;
  return new ApiError({
    message: status.message || `Request failed with status ${httpStatus}`,
    httpStatus,
    grpcCode,
    grpcStatus: grpcCode !== undefined ? GRPC_STATUS_NAMES[grpcCode] : undefined,
    details: status.details,
    retryAfterSeconds,
  });
}

export function networkError(cause: unknown): ApiError {
  return new ApiError({
    message: cause instanceof Error && cause.message ? cause.message : "Network request failed",
    httpStatus: 0,
    isNetworkError: true,
  });
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}
